import { Controller } from "@hotwired/stimulus"
import { isMac } from "../../helpers/platformHelpers"

export default class extends Controller {
  static values = {
    shortcuts: { type: Array, default: [] },
    debug: { type: Boolean, default: process.env.NODE_ENV === null || process.env.NODE_ENV === "development" }
  }

  connect() {
    this.shortcuts = new Map()
    this.isMac = isMac()

    this.primaryModifierKey = this.isMac ? "meta" : "ctrl"

    this.modifierKeyDisplayNames = {
      "meta": "Cmd",
      "ctrl": "Ctrl"
    }

    this.log("ShortcutsManager connected", {
      platform: this.isMac ? "Mac" : "Other",
      modifierKey: this.modifierKeyDisplayNames[this.primaryModifierKey]
    })

    this.keydownHandler = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this.keydownHandler)

    window.shortcutsManager = this
    this.log("ShortcutsManager available at window.shortcutsManager")
  }

  disconnect() {
    document.removeEventListener("keydown", this.keydownHandler)
  }

  registerShortcut(key, withModifier = false, action = null, target = null) {
    const normalizedKey = this.normalizeKey(key)
    const modifiers = withModifier ? [this.primaryModifierKey] : []
    const keyConfig = this.buildKeyConfig(normalizedKey, modifiers)
    const shortcutKey = this.buildShortcutKey(keyConfig)

    const shortcut = {
      keyConfig,
      action,
      target: target || document
    }

    this.shortcuts.set(shortcutKey, shortcut)

    this.log("Registered shortcut", {
      key: keyConfig.key,
      modifiers: keyConfig.modifiers,
      action,
      target: target?.tagName || "document"
    })
  }

  unregisterShortcut(key, withModifier = false) {
    const normalizedKey = this.normalizeKey(key)
    const modifiers = withModifier ? [this.primaryModifierKey] : []
    const keyConfig = this.buildKeyConfig(normalizedKey, modifiers)
    const shortcutKey = this.buildShortcutKey(keyConfig)
    const existed = this.shortcuts.has(shortcutKey)
    this.shortcuts.delete(shortcutKey)

    if (existed) {
      this.log("Unregistered shortcut", {
        key: keyConfig.key,
        modifiers: keyConfig.modifiers
      })
    }
  }

  handleKeydown(event) {
    if (this.isTyping(event.target)) {
      this.log("Ignoring keydown in input field", { key: event.key, target: event.target.tagName })
      return
    }

    const eventModifiers = this.getEventModifiers(event)
    const keyConfig = this.buildKeyConfig(event.key, eventModifiers)
    const shortcutKey = this.buildShortcutKey(keyConfig)

    this.log("Keydown event", {
      key: event.key,
      modifiers: eventModifiers
    })

    const shortcut = this.shortcuts.get(shortcutKey)

    if (shortcut) {
      this.log("Triggering shortcut", {
        key: shortcut.keyConfig.key,
        modifiers: shortcut.keyConfig.modifiers,
        action: shortcut.action
      })
      event.preventDefault()
      this.triggerShortcut(shortcut, event)
    }
  }

  triggerShortcut(shortcut, originalEvent) {
    if (shortcut.action) {
      const customEvent = new CustomEvent(`shortcut:${shortcut.action}`, {
        detail: {
          originalEvent,
          shortcut
        },
        bubbles: true
      })

      shortcut.target.dispatchEvent(customEvent)
    }
  }

  buildKeyConfig(key, modifiers = []) {
    return {
      key: key,
      modifiers: this.sortModifiers(modifiers)
    }
  }

  buildShortcutKey(keyConfig) {
    return JSON.stringify(keyConfig)
  }

  sortModifiers(modifiers) {
    const order = ["ctrl", "alt", "shift", "meta"]
    return modifiers.sort((a, b) => order.indexOf(a) - order.indexOf(b))
  }

  getEventModifiers(event) {
    const modifiers = []
    if (event.metaKey) modifiers.push("meta")
    if (event.ctrlKey) modifiers.push("ctrl")
    if (event.altKey) modifiers.push("alt")
    if (event.shiftKey) modifiers.push("shift")
    return this.sortModifiers(modifiers)
  }

  normalizeKey(key) {
    if (key.length === 1 && key !== key.toLowerCase()) {
      console.warn(`[ShortcutsManager] Capital letter "${key}" should be registered as lowercase "${key.toLowerCase()}" with shift modifier explicitly`)
      return key.toLowerCase()
    }
    return key
  }

  isTyping(element) {
    const typingElements = ["INPUT", "TEXTAREA", "SELECT"]
    return typingElements.includes(element.tagName) ||
      element.contentEditable === "true"
  }

  toggleDebug() {
    this.debugValue = !this.debugValue
    this.log(`Debug mode ${this.debugValue ? "enabled" : "disabled"}`)
    return this.debugValue
  }

  log(message, data = null) {
    if (this.debugValue) {
      console.log(`[ShortcutsManager] ${message}`, data || "")
    }
  }
}
